import type { DebugAction } from './actions';

type DebugActionObserver = (action: DebugAction) => void;

/** Array of tuples of observers + (optionally) the subset of types that observer observes. */
const observers: { observer: DebugActionObserver; types?: DebugAction['type'][] }[] = [];

/**
 * Register an observer to listen for internal Blizzard actions.
 *
 * @param observer The observer - will be invoked with each action sequentially
 * @param eventTypes If provided, only debug actions of these types will be emitted to this
 *   observer. if not provided, all debug actions will be emitted.
 * @returns A function which can be invoked to remove the added observer
 */
export function registerDebugObserver(
  observer: DebugActionObserver,
  eventTypes?: DebugAction['type'][]
): () => void {
  const entry = { observer, types: eventTypes };
  observers.push(entry);
  return () => {
    observers.splice(observers.indexOf(entry), 1);
  };
}

/**
 * Log an internal action, emitting it to all interested observers registered via
 * {@link registerDebugObserver}
 *
 * @internal
 */
export function logDebugAction(action: DebugAction): void {
  observers.forEach(({ observer, types }) => {
    if (types?.includes(action.type) ?? true) {
      observer(action);
    }
  });
}

export { type DebugAction } from './actions';
